/**
 * Brand resolution — turns the "empty = derive" `StorefrontConfig.brand` block
 * into a fully-resolved set of brand terms, and expands the `{brand}` / `{BRAND}`
 * / `{company}` / `{wallet}` / `{kit}` / `{kits}` / `{year}` tokens that the storefront defaults (and
 * any admin-typed copy) carry so a single brand name drives every surface.
 *
 * Why tokens instead of literals in the defaults: a tenant that only sets a
 * brand name (or none — just `tenant_config.name`) gets fully branded copy with
 * zero per-string editing, and the shipped defaults never leak a hardcoded brand.
 *
 * Both functions are PURE — `applyBrandTokens` returns a new config and never
 * mutates its input.
 */

import type { StorefrontConfig } from './storefront-content';

/** Terminal brand fallback — only reached when neither a brand block, a
 * customized footer brand, nor a tenant name is available (nearly impossible,
 * since `tenant_config.name` is NOT NULL). */
export const BRAND_FALLBACK = 'Store';

export type ResolvedBrand = {
  brandName: string;
  siteTitle: string;
  companyName: string;
  houseStoreName: string;
  marketplaceSellerName: string;
  mallBadgeText: string;
  showMallBadge: boolean;
  walletLabel: string;
  /** "kit" by default; the tenant's `brand.kitLabel` (e.g. "course") when set. */
  kitLabel: string;
  /** "kits" / `${kitLabel}s`. */
  kitLabelPlural: string;
  /** "Kit" / capitalize-first(kitLabel). */
  kitLabelCap: string;
  /** "activation kit" — collapses to the bare custom noun when set. */
  activationKitLabel: string;
  /** "activation kits" — collapses to the plural custom noun when set. */
  activationKitLabelPlural: string;
  /** "membership kit" — collapses to the bare custom noun when set. */
  membershipKitLabel: string;
};

const BRAND_TOKEN_RE = /\{(brand|BRAND|company|wallet|kit|kits|year)\}/g;

/** A stored footer brand is only treated as a real customization when it is not
 * empty, not the tokenized default (`{brand}`), and not the legacy 'Omni'. */
function isDerivableFooterBrand(s: string): boolean {
  const v = s.trim();
  return v !== '' && v !== 'Omni' && !/\{(brand|BRAND|company|wallet|kit|kits|year)\}/.test(v);
}

/**
 * Resolve the activation-kit vocabulary from the stored `brand.kitLabel`.
 * Empty = the standard kit wording, byte-identical to the historical literals;
 * a custom noun ("course") replaces the whole family — the "activation" /
 * "membership" qualifiers exist to disambiguate KIT, so a specific noun drops
 * them ("Pick your course", not "Pick your activation course"). Independent of
 * `brandName` / the fallback state on purpose: terminology, not identity.
 */
function resolveKitTerms(raw: string | null | undefined): Pick<
  ResolvedBrand,
  | 'kitLabel'
  | 'kitLabelPlural'
  | 'kitLabelCap'
  | 'activationKitLabel'
  | 'activationKitLabelPlural'
  | 'membershipKitLabel'
> {
  const c = (raw ?? '').trim();
  if (!c) {
    return {
      kitLabel: 'kit',
      kitLabelPlural: 'kits',
      kitLabelCap: 'Kit',
      activationKitLabel: 'activation kit',
      activationKitLabelPlural: 'activation kits',
      membershipKitLabel: 'membership kit',
    };
  }
  const plural = `${c}s`;
  return {
    kitLabel: c,
    kitLabelPlural: plural,
    kitLabelCap: c.charAt(0).toUpperCase() + c.slice(1),
    activationKitLabel: c,
    activationKitLabelPlural: plural,
    membershipKitLabel: c,
  };
}

/**
 * Resolve the brand block against its fallbacks. `tenantName` comes from
 * `tenant_config.name` (via `GET /tenant/config`); pass it so a tenant that
 * never edited the brand block still gets its own name everywhere.
 */
export function resolveBrand(cfg: StorefrontConfig, tenantName?: string | null): ResolvedBrand {
  const b = cfg.brand;
  const clean = (v: string | null | undefined): string => (v ?? '').trim();

  let brandName = clean(b?.brandName);
  if (!brandName) {
    const footerBrand = clean(cfg.footer?.brandName);
    if (isDerivableFooterBrand(footerBrand)) brandName = footerBrand;
  }
  if (!brandName) brandName = clean(tenantName);

  // Bare fallback → drop the "{brand} " prefix from derivations so we never
  // render awkward strings like "Store Official Store".
  const isFallback = brandName === '';
  if (isFallback) brandName = BRAND_FALLBACK;

  // The tab title falls back to the bare brand name; unlike the other terms it
  // gets no "{brand} …" decoration, so the fallback tenant reads just "Store".
  const siteTitle = clean(b?.siteTitle) || brandName;
  const companyName = clean(b?.companyName) || brandName;
  const houseStoreName =
    clean(b?.houseStoreName) || (isFallback ? 'Official Store' : `${brandName} Official Store`);
  const marketplaceSellerName =
    clean(b?.marketplaceSellerName) ||
    (isFallback ? 'Marketplace Seller' : `${brandName} Marketplace Seller`);
  const mallBadgeText =
    clean(b?.mallBadgeText) || (isFallback ? 'MALL' : `${brandName.toUpperCase()} MALL`);
  const walletLabel = clean(b?.walletLabel) || (isFallback ? 'Wallet' : `${brandName} Wallet`);
  const showMallBadge = b?.showMallBadge !== false;

  return {
    brandName,
    siteTitle,
    companyName,
    houseStoreName,
    marketplaceSellerName,
    mallBadgeText,
    showMallBadge,
    walletLabel,
    ...resolveKitTerms(b?.kitLabel),
  };
}

/**
 * Replace every brand token in every string of the config with its resolved
 * value. Non-token strings, hrefs, numbers and booleans pass through untouched;
 * the input is not mutated. `year` is injectable for deterministic tests
 * (defaults to the current calendar year).
 */
export function applyBrandTokens(
  cfg: StorefrontConfig,
  brand: ResolvedBrand,
  year?: number,
): StorefrontConfig {
  const yr = String(year ?? new Date().getFullYear());
  const map: Record<string, string> = {
    '{brand}': brand.brandName,
    '{BRAND}': brand.brandName.toUpperCase(),
    '{company}': brand.companyName,
    '{wallet}': brand.walletLabel,
    '{kit}': brand.kitLabel,
    '{kits}': brand.kitLabelPlural,
    '{year}': yr,
  };
  const replace = (s: string): string => s.replace(BRAND_TOKEN_RE, (m) => map[m] ?? m);
  return walk(cfg, replace) as StorefrontConfig;
}

function walk(value: unknown, fn: (s: string) => string): unknown {
  if (typeof value === 'string') return fn(value);
  if (Array.isArray(value)) return value.map((v) => walk(v, fn));
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = walk(v, fn);
    return out;
  }
  return value;
}
